import { useQueryClient } from '@tanstack/react-query';
import { ApiError, useApi } from '@/hooks/useApi';
import { useModeAwareMutation } from '@/hooks/useModeAwareQuery';

export interface LogCaseEmailRequest {
  sourceId: string;
  toRecipients: string[];
  ccRecipients?: string[];
  subject: string;
  htmlBody: string;
  templateId?: number;
  templateName?: string;
  isSubjectModified: boolean;
  isBodyModified: boolean;
  attachOutstandingInvoices: boolean;
  attachedTransactionIds?: number[];
  /** Set when the merge-data build reported the template's payment link as below the PaySuite minimum. */
  paymentLinkOmitted?: boolean;
}

export interface StageSkipConfirmation {
  nextStageSortOrder: number;
  nextStageName: string;
}

export interface LogCaseEmailResult {
  stageConfirmation: StageSkipConfirmation | null;
}

export function useLogCaseEmail() {
  const { apiBaseUrl, getHeaders } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<LogCaseEmailResult, Error, LogCaseEmailRequest>(
    async (request) => {
      const { sourceId, ...body } = request;
      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/collection-cases/${sourceId}/communications/log-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
      }

      const data = await response.json().catch(() => null);
      return { stageConfirmation: data?.stageConfirmation ?? null };
    },
    () => {
      // Demo mode: no-op, no strategy advancement
      return { stageConfirmation: null };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['collection-case-detail'] });
        queryClient.invalidateQueries({ queryKey: ['case-recent-activities'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
      },
    },
  );
}

export interface StageSkipDecisionRequest {
  caseId: string;
  nextStageSortOrder: number;
  markComplete: boolean;
}

export function useRecordStageSkipDecision() {
  const { apiBaseUrl, getHeaders } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, StageSkipDecisionRequest>(
    async (request) => {
      const { caseId, ...body } = request;
      const headers = await getHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/collection-cases/${caseId}/communications/stage-skip-decision`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
      }
    },
    () => {
      // Demo mode: no-op
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['collection-case-detail'] });
        queryClient.invalidateQueries({ queryKey: ['case-recent-activities'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
      },
    },
  );
}
